// ─── Impuestos de una causación ──────────────────────────────────────────────
// Una sola puerta para el IVA y las retenciones de todo lo que la empresa DEBE:
// la factura del proveedor (desde una OCP) y la cuenta por pagar directa que
// nace de un costo asociado a una orden. Tener dos cálculos distintos para lo
// mismo fue el origen de un IVA descontable inventado (ver CLAUDE.md), así que
// aquí vive el único.
import { DEFAULT_PARAMETROS_FISCALES } from "./config.js";
import { getParametrosFiscales } from "./cloudSync.js";
import { desgloseIVA } from "./iva.js";
import { fmtCOP, parseNumber, parsePct } from "./utils.js";

// `lineas` es opcional: cuando se pasan, el IVA se calcula LÍNEA POR LÍNEA.
// Hace falta porque una misma factura de proveedor puede mezclar un bien
// gravado con uno que la ley no grava — la compra típica de Mathiz: la matera
// lleva IVA y la mata viva no (art. 424, partidas 06.01 y 06.02). Sin las
// líneas se cae al cálculo sobre el subtotal, que es lo que había antes.
// `opts.baseMinimaUVT` cambia la cuantía mínima de retefuente. Hace falta
// porque la ley la fija por CONCEPTO y no es la misma: compras generales 27 UVT
// (art. 401 E.T. / Dcto. 392 de 2021) pero SERVICIOS apenas 4 UVT (art. 392
// E.T.). Sin esto, un transporte de $800.000 no llevaba retención por quedar
// bajo el mínimo de las compras ($1.414.098), cuando el mínimo que le aplica
// son $209.496 y sí debe retenerse.
export function calcularImpuestosCausacion(prov, sub, lineas, opts) {
  prov = prov || {};
  const pf = getParametrosFiscales();
  const uvtCOP = parseNumber(pf.uvt) || DEFAULT_PARAMETROS_FISCALES.uvt;
  sub = parseNumber(sub) || 0;

  // ── IVA: solo si el proveedor está marcado como responsable de IVA ──────
  const esResponsableIVA = !!prov.responsable_iva;
  const ivaPct = parseNumber(pf.iva_general_pct) ?? 19;
  const desglose = Array.isArray(lineas) && lineas.length ? desgloseIVA(lineas) : null;
  const iva = !esResponsableIVA ? 0
    : (desglose ? desglose.iva : Math.round(sub * ivaPct / 100));
  const motivoIVANoAplica = esResponsableIVA ? "" : "Proveedor NO responsable de IVA — la factura no causa IVA";
  const baseNoGravada = desglose ? desglose.baseExenta + desglose.baseExcluida : 0;

  // ── ReteFuente (compras) ─────────────────────────────────────────────────
  const esRST = (prov.regimen_fiscal || "ordinario") === "rst";
  const esAutorretenedor = !!prov.autorretenedor;
  const rfPct = parsePct(prov.retefuente);
  const rfPctEfectivo = (rfPct != null && rfPct !== "" && !isNaN(rfPct)) ? rfPct : 0;
  const baseUVTRF = (opts && opts.baseMinimaUVT != null)
    ? parseNumber(opts.baseMinimaUVT)
    : (parseNumber(pf.retefuente_compras_base_uvt) || 0);
  const baseMinimaRF = baseUVTRF * uvtCOP;
  let montoRF = 0, motivoRFNoAplica = "";
  if (rfPctEfectivo <= 0) {
    motivoRFNoAplica = "Sin % de ReteFuente configurado para este tercero";
  } else if (esRST && pf.rst_exonera_retefuente) {
    motivoRFNoAplica = "Régimen Simple (RST) — exonerado de retefuente por renta (Art. 911 E.T.)";
  } else if (esAutorretenedor && pf.autorretenedor_exonera_retefuente) {
    motivoRFNoAplica = "Proveedor autorretenedor — se autorretiene, no se le practica retefuente";
  } else if (sub < baseMinimaRF) {
    motivoRFNoAplica = `Base (${fmtCOP(sub)}) inferior al mínimo de ${baseUVTRF} UVT (${fmtCOP(baseMinimaRF)})`;
  } else {
    montoRF = Math.round(sub * rfPctEfectivo / 100);
  }

  // ── ReteICA (tarifa según el municipio/actividad — configurada por tercero) ─
  const ricaPct = parsePct(prov.reteica);
  const ricaPctEfectivo = (ricaPct != null && ricaPct !== "" && !isNaN(ricaPct)) ? ricaPct : 0;
  let montoRICA = 0, motivoRICANoAplica = "";
  if (ricaPctEfectivo <= 0) {
    motivoRICANoAplica = "Sin % de ReteICA configurado para este tercero";
  } else if (esRST && pf.rst_exonera_reteica) {
    motivoRICANoAplica = "Régimen Simple (RST) — el componente de ICA queda integrado al RST";
  } else {
    montoRICA = Math.round(sub * ricaPctEfectivo / 100);
  }

  // ── ReteIVA: solo aplica sobre IVA efectivamente generado ────────────────
  const rivaPctConfig = parsePct(prov.reteiva);
  // Nota: el % de ReteIVA SOLO se toma del tercero (es un acuerdo/perfil específico,
  // no se inventa una retención global que el proveedor no tenga configurada).
  const rivaPct = (rivaPctConfig != null && rivaPctConfig !== "" && !isNaN(rivaPctConfig)) ? rivaPctConfig : 0;
  let montoRIVA = 0, motivoRIVANoAplica = "";
  if (!esResponsableIVA || iva <= 0) {
    motivoRIVANoAplica = "No hay IVA generado en esta factura — no hay base para ReteIVA";
  } else if (rivaPct <= 0) {
    motivoRIVANoAplica = "Sin % de ReteIVA configurado";
  } else {
    montoRIVA = Math.round(iva * rivaPct / 100);
  }

  const total = sub + iva;
  const totalRetenciones = montoRF + montoRICA + montoRIVA;
  const neto = total - totalRetenciones;

  return {
    sub, iva, ivaPct, esResponsableIVA, motivoIVANoAplica, total,
    // Lo que en esta factura NO causa IVA, para poder decirlo en pantalla en
    // vez de dejar al usuario preguntándose por qué el IVA no es el 19% del
    // subtotal.
    baseNoGravada, desgloseIVA: desglose,
    rfPct: rfPctEfectivo, montoRF, motivoRFNoAplica,
    ricaPct: ricaPctEfectivo, montoRICA, motivoRICANoAplica,
    rivaPct, montoRIVA, motivoRIVANoAplica,
    totalRetenciones, neto
  };
}
